import {
  Component,
  ElementRef,
  Renderer2,
  OnInit,
  Input,
  OnDestroy
} from '@angular/core';
import { BehaviorSubject, Subject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, filter, takeUntil, catchError } from 'rxjs/operators';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import {
  UpgradeLogFilter,
  SessionSummary,
} from './upgrade-logs-viewer.model';
import { UpgradeLogsViewerService } from './services/upgrade-logs-viewer.service';
import { UpgradeLogsFilterService } from './services/upgrade-logs-filter.service';

const getLabels = () => ({
  title: 'Upgrade Logs Viewer',
  searchPlaceholder: 'Enter device serial number',
  refreshButton: 'Refresh',
  noDataMessage: 'No upgrade sessions found',
  loadingMessage: 'Loading upgrade sessions...',
  exportSession: 'Export Session',
  deviceSerial: 'Device Serial',
  dateRange: 'Date Range',
  status: 'Status',
  operator: 'Operator',
  search: 'Search',
  clear: 'Clear',
  sessionDetails: 'Session Details',
  summary: 'Summary',
  events: 'Events',
  sgpCommands: 'SGP Commands',
  deviceSnapshot: 'Device Snapshot'
});

@CreateCustomBlockSizeType({
  'breakpoint-768': 768,   // Mobile
  'breakpoint-1024': 1024, // Tablet
  'breakpoint-1200': 1200  // Desktop
})
@Component({
  selector: 'lib-upgrade-logs-viewer',
  templateUrl: './upgrade-logs-viewer.component.html',
  styleUrls: ['./upgrade-logs-viewer.component.scss'],
})
export class UpgradeLogsViewerComponent implements OnInit, OnDestroy, ICustomBlock {
  private destroy$ = new Subject<void>();

  // ICustomBlock implementation
  width?: number;
  currentWidth?: number;

  constructor(
    public el: ElementRef,
    public renderer: Renderer2,
    private logsService: UpgradeLogsViewerService,
    private filterService: UpgradeLogsFilterService
  ) {}

  // State management
  readonly currentFilter$ = new BehaviorSubject<UpgradeLogFilter>({});
  readonly selectedSession$ = new BehaviorSubject<SessionSummary | null>(null);
  readonly loading$ = new BehaviorSubject<boolean>(false);

  // Data streams
  readonly sessions$ = this.currentFilter$.pipe(
    debounceTime(300),
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    switchMap(filter => {
      if (!filter.deviceSerial?.trim()) {
        return new Observable<SessionSummary[]>(observer => observer.next([]));
      }

      this.loading$.next(true);
      return this.logsService.searchDeviceUpgrades(filter.deviceSerial).pipe(
        switchMap(sessions => {
          const filteredSessions = this.filterService.applyFilters(sessions, filter);
          this.loading$.next(false);
          return new Observable<SessionSummary[]>(observer => observer.next(filteredSessions));
        })
      );
    }),
    takeUntil(this.destroy$)
  );

  readonly sessionDetails$ = this.selectedSession$.pipe(
    filter(session => !!session),
    switchMap(session => {
      return this.logsService.getSessionDetails(session!.deviceSerial, session!.sessionId, session!).pipe(
        catchError(error => {
          console.error('Error loading session details:', error);
          // Return a mock session details for testing
          return of({
            session: {
              sessionId: session!.sessionId,
              deviceSerial: session!.deviceSerial,
              deviceModel: session!.deviceModel,
              operatorId: 'test',
              operatorName: session!.operatorName,
              operatorEmail: 'test@example.com',
              status: session!.status,
              startTime: session!.startTime,
              endTime: session!.endTime,
              lastUpdated: Date.now(),
              epochStart: session!.startTime,
              modules: (session!.modules || []).map(module => ({
                name: module,
                status: 'pending' as any
              })),
              preUpgradeSnapshot: {
                timestamp: session!.startTime,
                inverterStatus: {},
                batteryDetails: {},
                captureMethod: 'pre_upgrade' as const
              }
            },
            events: [],
            sgpCommands: [],
            phases: [],
            deviceSnapshot: {
              timestamp: session!.startTime,
              inverterStatus: {},
              batteryDetails: {},
              captureMethod: 'pre_upgrade' as const
            },
            statistics: {
              totalEvents: 0,
              totalSgpCommands: 0,
              totalErrors: 0,
              averageCommandTime: 0,
              phaseDurations: {}
            }
          });
        })
      );
    }),
    takeUntil(this.destroy$)
  );

  // Labels for i18n
  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  get labels() {
    return this._labels;
  }

  ngOnInit(): void {
    // Initialize with empty filter
    this.currentFilter$.next({});
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Public methods
  onSearchDevice(deviceSerial: string): void {
    const currentFilter = this.currentFilter$.value;
    this.currentFilter$.next({
      ...currentFilter,
      deviceSerial: deviceSerial.trim()
    });
  }

  onFilterChange(filter: UpgradeLogFilter): void {
    this.currentFilter$.next(filter);
  }

  onSelectSession(session: SessionSummary): void {
    this.selectedSession$.next(session);
  }

  onRefresh(): void {
    const currentFilter = this.currentFilter$.value;
    // Trigger refresh by emitting the same filter
    this.currentFilter$.next({ ...currentFilter });
  }

  onClearSelection(): void {
    this.selectedSession$.next(null);
  }

  async onExportSession(session: SessionSummary): Promise<void> {
    try {
      const blob = await this.logsService.exportSessionToJson(
        session.deviceSerial,
        session.sessionId
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `upgrade-session-${session.deviceSerial}-${session.sessionId}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export session:', error);
    }
  }

  async onExportSgpCommands(session: SessionSummary): Promise<void> {
    try {
      const blob = await this.logsService.exportSgpCommandsToText(
        session.deviceSerial,
        session.sessionId
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sgp-commands-${session.deviceSerial}-${session.sessionId}.txt`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export SGP commands:', error);
    }
  }

  formatDuration(milliseconds?: number): string {
    return this.filterService.formatDuration(milliseconds);
  }

  formatTimestamp(timestamp: number): string {
    return this.filterService.formatTimestamp(timestamp);
  }
}
